import React, {useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import styles from '../RewardSetup.module.scss'
import ProductsTable from './ProductsTable'
import ProductDetailsModal from './ProductDetailsModal'
import BulkProductDetailsModal from './BulkProductDetailsModal'
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert'

/**
 * ProductsCard component displays the products section with warning alert and table
 */
const ProductsCard = ({products = [], productsLoading = false, onUpdateProducts, onDeleteProduct}) => {
  // Modal states
  const [showSingleModal, setShowSingleModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  // Check if all products have complete details
  const areAllDetailsComplete = useMemo(() => {
    return products.every(
      product =>
        product.details.points &&
        product.details.inventory &&
        (product.details.quota.unlimited || product.details.quota.value)
    )
  }, [products])

  // Handle move up action
  const handleMoveUp = index => {
    if (index === 0) return // Can't move first item up

    const newProducts = [...products]
    const temp = newProducts[index]
    newProducts[index] = newProducts[index - 1]
    newProducts[index - 1] = temp

    // Update priorities
    const updatedProducts = newProducts.map((product, idx) => ({
      ...product,
      priority: idx + 1,
    }))

    onUpdateProducts(updatedProducts)
  }

  // Handle move down action
  const handleMoveDown = index => {
    if (index === products.length - 1) return // Can't move last item down

    const newProducts = [...products]
    const temp = newProducts[index]
    newProducts[index] = newProducts[index + 1]
    newProducts[index + 1] = temp

    // Update priorities
    const updatedProducts = newProducts.map((product, idx) => ({
      ...product,
      priority: idx + 1,
    }))

    onUpdateProducts(updatedProducts)
  }

  // Handle edit action
  const handleEdit = (product, index) => {
    setSelectedProduct(product)
    setSelectedIndex(index)
    setShowSingleModal(true)
  }

  // Handle delete action
  const handleDelete = index => {
    const productToDelete = products[index]
    if (productToDelete?.productId) {
      onDeleteProduct(productToDelete.productId)
    }
  }

  // Handle single product update
  const handleSingleProductUpdate = updatedDetails => {
    const newProducts = [...products]
    newProducts[selectedIndex] = {
      ...newProducts[selectedIndex],
      details: updatedDetails,
    }
    onUpdateProducts(newProducts)
  }

  // Handle bulk product update
  const handleBulkProductUpdate = updatedProducts => {
    onUpdateProducts(updatedProducts)
  }

  // Handle add details button click
  const handleAddDetails = () => {
    setShowBulkModal(true)
  }

  // Handle modal close
  const handleCloseSingleModal = () => {
    setShowSingleModal(false)
    setSelectedProduct(null)
    setSelectedIndex(-1)
  }

  const handleCloseBulkModal = () => {
    setShowBulkModal(false)
  }

  // Handle cancel actions
  const handleCancel = () => {
    // No action needed for cancel
  }

  return (
    <div className={`flex flex-col gap-6 rounded-lg p-6 ${styles.rewardSetup__card}`}>
      {/* Warning Alert */}
      {!areAllDetailsComplete && (
        <DuplicateAlert
          type="warning"
          message={{
            subTitle: 'Please add details for each product.',
            buttonText: 'Add Details',
          }}
          show={true}
          handleClick={handleAddDetails}
          iconTrailing="arrow_right_alt"
        />
      )}
      <h3 className="text-3xl font-bold text-white">Products</h3>

      {/* Products Table */}
      <ProductsTable
        products={products}
        productsLoading={productsLoading}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Single Product Edit Modal */}
      <ProductDetailsModal
        showModal={showSingleModal}
        onCloseModal={handleCloseSingleModal}
        product={selectedProduct}
        onApply={handleSingleProductUpdate}
        onCancel={handleCancel}
      />

      {/* Bulk Product Edit Modal */}
      <BulkProductDetailsModal
        showModal={showBulkModal}
        onCloseModal={handleCloseBulkModal}
        products={products}
        onApply={handleBulkProductUpdate}
        onCancel={handleCancel}
      />
    </div>
  )
}

ProductsCard.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      productName: PropTypes.string,
      priority: PropTypes.number,
      details: PropTypes.shape({
        points: PropTypes.string,
        quota: PropTypes.shape({
          unlimited: PropTypes.bool,
          value: PropTypes.string,
        }),
        inventory: PropTypes.string,
      }),
    })
  ),
  productsLoading: PropTypes.bool,
  onUpdateProducts: PropTypes.func.isRequired,
  onDeleteProduct: PropTypes.func.isRequired,
}

export default ProductsCard
